import React, { useCallback } from "react";
import { useLazyQuery } from "react-apollo";
import AddToRequisitionListQuery from "./AddToRequisitionListQuery.gql";
import useShouldDisplayCommandStatusTimeout from "theme/modules/Wishlist/AddProductToWishlist/useShouldDisplayCommandStatusTimeout";

/**
 * @callback LoadRequisitionListsFunction
 */

/**
 * @typedef {object} RequisitionList
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {object} InputProps
 * @property {import('theme/modules/User/helpers/withEnsureLoggedIn').EnsureLoggedInFunction} ensureLoggedIn
 * @property {import('theme/modules/User/helpers/withEnsureLoggedIn').ReloadIfNewLoginFunction} reloadIfNewLogin
 */

/**
 * @typedef {object} OutputProps
 * @property {LoadRequisitionListsFunction} loadRequisitionLists
 * @property {RequisitionList[]} [requisitionLists]
 * @property {boolean} [loadingRequisitionLists]
 * @property {string} [loadRequisitionListsError]
 */

/**
 * @param {React.ComponentType<BaseProps & InputProps & OutputProps>} BaseComponent
 * @returns {React.FunctionComponent<BaseProps & InputProps>}
 * @template BaseProps
 */
const withLoadRequisitionLists = (BaseComponent) => (props) => {
  const { ensureLoggedIn, reloadIfNewLogin } = props;

  const [
    addProductToRequisitionListQuery,
    {
      loading: loadingRequisitionLists,
      error: loadRequisitionListsError,
      data: loadRequisitionListsData,
    },
  ] = useLazyQuery(AddToRequisitionListQuery);

  const showLoadStatus = useShouldDisplayCommandStatusTimeout(
    2500,
    loadRequisitionListsError || loadRequisitionListsData,
    loadingRequisitionLists
  );

  const error = showLoadStatus ? loadRequisitionListsError?.message : null;

  const loadRequisitionLists = useCallback(() => {
    return ensureLoggedIn().then((needsReload) => {
      reloadIfNewLogin();
      if (!needsReload.current) {
        addProductToRequisitionListQuery();
      }
    });
  }, [ensureLoggedIn, reloadIfNewLogin, addProductToRequisitionListQuery]);

  const requisitionLists =
    loadingRequisitionLists || loadRequisitionListsError
      ? null
      : loadRequisitionListsData?.me.requisitionLists;

  return (
    <BaseComponent
      {...props}
      loadRequisitionLists={loadRequisitionLists}
      requisitionLists={requisitionLists}
      loadingRequisitionLists={loadingRequisitionLists}
      loadRequisitionListsError={error}
    />
  );
};

export default withLoadRequisitionLists;
